import type { Stage, StageTreeNode } from "../types.ts";

export function childrenCount(stages: Stage[]): Map<string, number> {
	const counts = new Map<string, number>();
	for (const stage of stages) {
		if (!stage.parentId) continue;
		counts.set(stage.parentId, (counts.get(stage.parentId) ?? 0) + 1);
	}
	return counts;
}

export function hasChildren(stages: Stage[], id: string): boolean {
	return stages.some((stage) => stage.parentId === id);
}

export function descendantIds(stages: Stage[], id: string): Set<string> {
	const ids = new Set<string>();
	const root = stages.find((stage) => stage.id === id);
	if (!root) return ids;
	for (const stage of stages.slice(stages.indexOf(root) + 1)) {
		if (stage.depth <= root.depth) break;
		ids.add(stage.id);
	}
	return ids;
}

function blockOf(stages: Stage[], id: string): Stage[] {
	const start = stages.findIndex((stage) => stage.id === id);
	const root = stages[start];
	if (!root) return [];
	const block = [root];
	for (const stage of stages.slice(start + 1)) {
		if (stage.depth <= root.depth) break;
		block.push(stage);
	}
	return block;
}

function arrayMove<T>(items: T[], from: number, to: number): T[] {
	const next = [...items];
	const [moved] = next.splice(from, 1);
	if (moved !== undefined) next.splice(to, 0, moved);
	return next;
}

export interface SiblingMove {
	stages: Stage[];
	changes: { stageId: string; position: number }[];
}

export function moveSiblingBlock(
	stages: Stage[],
	activeId: string,
	overId: string,
): SiblingMove | null {
	const active = stages.find((stage) => stage.id === activeId);
	const over = stages.find((stage) => stage.id === overId);
	if (!active || !over || active.id === over.id) return null;
	if ((active.parentId ?? "") !== (over.parentId ?? "")) return null;

	const siblings = stages.filter(
		(stage) => (stage.parentId ?? "") === (active.parentId ?? ""),
	);
	const reordered = arrayMove(
		siblings,
		siblings.indexOf(active),
		siblings.indexOf(over),
	);

	const blocks = reordered.flatMap((sibling) => blockOf(stages, sibling.id));
	const first = siblings[0];
	if (!first) return null;
	const regionStart = stages.indexOf(first);
	const next = [
		...stages.slice(0, regionStart),
		...blocks,
		...stages.slice(regionStart + blocks.length),
	];

	const changes = reordered
		.map((sibling, index) => ({ stageId: sibling.id, position: index }))
		.filter(({ stageId, position }) => {
			const sibling = siblings.find((stage) => stage.id === stageId);
			return sibling?.position !== position;
		});

	return { stages: next, changes };
}

export function visibleStages(
	stages: Stage[],
	collapsed: Set<string>,
): Stage[] {
	const visible: Stage[] = [];
	let skipDeeperThan: number | null = null;
	for (const stage of stages) {
		if (skipDeeperThan !== null && stage.depth > skipDeeperThan) continue;
		skipDeeperThan = collapsed.has(stage.id) ? stage.depth : null;
		visible.push(stage);
	}
	return visible;
}

export function effectivelyCancelledIds(stages: StageTreeNode[]): Set<string> {
	const byId = new Map(stages.map((stage) => [stage.id, stage]));
	const memo = new Map<string, boolean>();

	function isCancelled(stage: StageTreeNode): boolean {
		const known = memo.get(stage.id);
		if (known !== undefined) return known;
		const parent = stage.parentId ? byId.get(stage.parentId) : undefined;
		const cancelled =
			stage.status === "cancelled" || (!!parent && isCancelled(parent));
		memo.set(stage.id, cancelled);
		return cancelled;
	}

	return new Set(stages.filter(isCancelled).map((stage) => stage.id));
}
